// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import { universes } from '../config/universes/UniverseConfig';
import './StarMap.css';

/**
 * StarMap Component
 * Navigation overview that launches the detailed 3D galactic map
 */
const StarMap = ({
  starSystems,
  distancePositions,
  currentLocation,
  selectedSystem,
  scannedSystems,
  navigationMode,
  onSystemSelect,
  onSetCourse,
  onStartScan,
  onExpandMap,
  getDisplayDistance
}) => {
  const [hoveredUniverse, setHoveredUniverse] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });
  
  const handleMapClick = () => {
    onExpandMap();
  };
  
  // Get current universe info
  const currentUniverseId = currentLocation?.universeId || 'milkyway';
  const currentUniverse = universes[currentUniverseId] || universes.milkyway; // Always fallback to milkyway
  
  // Get all warp gates in current universe to show connections
  const currentUniverseGates = starSystems.filter(s => s.type === 'warpgate');
  
  // Build universe connection map
  // Show all gate destinations, not just discovered ones - gates are how you discover universes!
  const universeConnections = {};
  currentUniverseGates.forEach(gate => {
    if (gate.destinationUniverse) {
      universeConnections[gate.destinationUniverse] = gate;
    }
  });

  return (
    <div className="star-map-container">
      {/* Universe Traversal Map - Click to open full map */}
      <div className="star-map" onClick={handleMapClick}>
        <div className="map-grid holo-grid">
          {/* Simplified background grid */}
          <div className="grid-lines">
            {[...Array(8)].map((_, i) => (
              <div key={`h-${i}`} className="grid-line horizontal" style={{top: `${i * 12.5}%`}}></div>
            ))}
            {[...Array(8)].map((_, i) => (
              <div key={`v-${i}`} className="grid-line vertical" style={{left: `${i * 12.5}%`}}></div>
            ))}
          </div>
          
          {/* Current Universe - Centered */}
          <div className="universe-node current">
            <div 
              className="universe-circle current-circle" 
              style={{
                borderColor: currentUniverse.theme.primary,
                boxShadow: `0 0 20px ${currentUniverse.theme.primary}, inset 0 0 15px ${currentUniverse.theme.primary}`
              }}
            />
            <div className="universe-name" style={{
              color: currentUniverse.theme.primary,
              textShadow: `0 0 10px ${currentUniverse.theme.primary}`
            }}>
              {currentUniverse.name.toUpperCase()}
            </div>
            <div className="universe-status">CURRENT UNIVERSE</div>
          </div>
          
          {/* Connected Universes */}
          {Object.entries(universeConnections).map(([universeId, gate], index) => {
            const targetUniverse = universes[universeId];
            if (!targetUniverse) return null;
            
            // Position connected universes around the center
            const angle = (index * (360 / Object.keys(universeConnections).length)) * (Math.PI / 180);
            const radius = 35; // percentage from center
            const x = 50 + radius * Math.cos(angle);
            const y = 50 + radius * Math.sin(angle);
            
            return (
              <React.Fragment key={universeId}>
                {/* Connection line */}
                <svg className="universe-connection-line" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 1
                }}>
                  <line
                    x1="50%"
                    y1="50%"
                    x2={`${x}%`}
                    y2={`${y}%`}
                    stroke={targetUniverse.theme.primary}
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    opacity="0.6"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="0"
                      to="10"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  </line>
                  
                  {/* Gate indicator on line */}
                  <circle
                    cx="50%"
                    cy="50%"
                    r="3"
                    fill="#ff00ff"
                    opacity="0.8"
                  >
                    <animateMotion
                      dur="3s"
                      repeatCount="indefinite"
                      path={`M 0 0 L ${(x - 50) * 2} ${(y - 50) * 2}`}
                    />
                  </circle>
                </svg>
                
                {/* Connected Universe Node */}
                <div 
                  className="universe-node connected"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    setHoveredUniverse(targetUniverse);
                    setTooltipPosition({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => {
                    e.stopPropagation();
                    setTooltipPosition({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    setHoveredUniverse(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div 
                    className="universe-circle connected-circle" 
                    style={{
                      borderColor: targetUniverse.theme.primary,
                      boxShadow: `0 0 15px ${targetUniverse.theme.primary}, inset 0 0 10px ${targetUniverse.theme.primary}`
                    }}
                  />
                  <div className="universe-name" style={{
                    color: targetUniverse.theme.primary,
                    textShadow: `0 0 8px ${targetUniverse.theme.primary}`
                  }}>
                    {targetUniverse.name.toUpperCase()}
                  </div>
                  <div className="gate-label" style={{
                    color: '#ff00ff',
                    textShadow: '0 0 8px #ff00ff'
                  }}>
                    VIA {gate.name}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          
          {/* Map title overlay */}
          <div className="map-title">UNIVERSE TRAVERSAL MAP</div>
        </div>
      </div>
      
      {/* Expand instruction */}
      <div className="map-expand-hint" onClick={handleMapClick}>
        <span className="expand-icon">⛶</span>
        <span className="expand-text">CLICK TO EXPAND MAP</span>
      </div>

      {/* Universe Hover Tooltip */}
      {hoveredUniverse && (
        <div 
          className="universe-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
            pointerEvents: 'none',
            zIndex: 10000
          }}
        >
          <div className="tooltip-title" style={{ color: hoveredUniverse.theme.primary }}>
            {hoveredUniverse.name}
          </div>
          <div className="tooltip-description">
            {hoveredUniverse.description}
          </div>
          <div className="tooltip-stats">
            <div className="tooltip-stat">
              <span className="stat-label">Theme:</span>
              <span className="stat-value">{hoveredUniverse.codename}</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StarMap;
